import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inputCls } from "@/components/cms/kit";
import { ImagePlus, Loader2, X, RefreshCw, CheckCircle2, Sparkles } from "lucide-react";

// Reusable, provider-agnostic AI Image Generation panel. Kept independent of any
// specific editor so Articles/News/Guides/Reviews/etc. can reuse it. Manual only —
// nothing is generated until the user clicks Generate; nothing attaches until Use Image.
export function ImageGenPanel({ context, onUseImage }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-klyroo-cyan/20 bg-klyroo-bg/40 p-4 mb-4" data-testid="image-gen-panel">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-klyroo-cyan"><ImagePlus size={13} /> AI Image</span>
        <span className="text-[10px] font-mono text-slate-500">manual · uses your configured image provider</span>
        <button onClick={() => setOpen(true)} data-testid="generate-ai-image-btn" className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-klyroo-cyan text-black text-sm font-semibold hover:bg-white"><Sparkles size={14} /> Generate AI Image</button>
      </div>
      {open && <ImageGenModal context={context} onUseImage={onUseImage} onClose={() => setOpen(false)} />}
    </div>
  );
}

function ImageGenModal({ context, onUseImage, onClose }) {
  const [providers, setProviders] = useState([]);
  const [catalog, setCatalog] = useState({});
  const [sizes, setSizes] = useState(["1536x1024", "1024x1024", "1024x1536"]);
  const [pid, setPid] = useState("");
  const [model, setModel] = useState("");
  const [size, setSize] = useState("1536x1024");
  const [direction, setDirection] = useState("");
  const [prompt, setPrompt] = useState("");
  const [promptTouched, setPromptTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [err, setErr] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.imageProviders().then((d) => { const l = d.providers || []; setProviders(l); const p = l.find((x) => x.configured && x.enabled) || l[0]; if (p) setPid(p.id); }).catch(() => {});
    api.imageCatalog().then((d) => { setCatalog(d.catalog || {}); if (d.sizes) setSizes(d.sizes); }).catch(() => {});
  }, []);

  const models = useMemo(() => (catalog[pid] || []).filter((m) => m.status !== "deprecated" && m.enabled !== false), [catalog, pid]);
  useEffect(() => { const r = models.find((m) => m.recommended) || models[0]; setModel(r ? r.id : ""); }, [pid, catalog]); // eslint-disable-line
  useEffect(() => {
    if (promptTouched) return;
    api.imagePrompt({ ...context, visual_direction: direction }).then((d) => setPrompt(d.prompt)).catch(() => {});
  }, [context, direction, promptTouched]);

  const active = providers.find((p) => p.id === pid);
  const ready = !!(active && active.configured && active.enabled && model);

  const generate = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await api.imageGenerate({ ...context, provider: pid, model, size, prompt, visual_direction: direction });
      if (r.success) setResult(r);
      else setErr(r.error || { code: "ERROR", message: "Failed" });
    } catch (e) { setErr({ code: "REQUEST_FAILED", message: e.response?.data?.detail || "Request failed" }); }
    finally { setBusy(false); }
  };
  const use = async () => {
    setApplying(true);
    try {
      const r = await api.imageApply({ generation_id: result.generation_id, image_url: result.image_url, article_slug: context.article_slug });
      onUseImage && onUseImage(r.image_url);
      toast.success("Featured image set");
      onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not attach image"); }
    finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-[155] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-2xl card-surface rounded-2xl p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="image-gen-modal">
        <div className="flex items-start justify-between gap-3 mb-4">
          <h3 className="font-display font-bold text-white text-lg inline-flex items-center gap-2"><ImagePlus size={18} className="text-klyroo-cyan" /> Generate AI Image</h3>
          <button onClick={onClose} data-testid="image-gen-close" className="p-1 text-slate-400 hover:text-white shrink-0"><X size={18} /></button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Provider</span>
            <select value={pid} onChange={(e) => setPid(e.target.value)} data-testid="image-provider" className={inputCls}>{providers.map((p) => <option key={p.id} value={p.id}>{p.name}{p.configured ? (p.enabled ? "" : " (disabled)") : " (not configured)"}</option>)}</select></label>
          <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Model</span>
            <select value={model} onChange={(e) => setModel(e.target.value)} data-testid="image-model" className={inputCls}>{models.map((m) => <option key={m.id} value={m.id}>{m.display_name}</option>)}</select></label>
          <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Aspect / size</span>
            <select value={size} onChange={(e) => setSize(e.target.value)} data-testid="image-size" className={inputCls}>{sizes.map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
          <label className="block"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Visual direction (optional)</span>
            <input value={direction} onChange={(e) => setDirection(e.target.value)} data-testid="image-direction" placeholder="e.g. moody, cinematic" className={inputCls} /></label>
        </div>

        <label className="block mb-3"><span className="text-[10px] font-mono uppercase tracking-widest text-slate-400">Prompt preview</span>
          <textarea value={prompt} onChange={(e) => { setPrompt(e.target.value); setPromptTouched(true); }} rows={4} data-testid="image-prompt-preview" className={`${inputCls} text-xs`} /></label>

        {!ready && <p className="text-xs text-amber-300 mb-2" data-testid="image-not-ready">Configure & enable an image provider in the AI / LLM settings to generate.</p>}
        {err && <div className="text-sm mb-2" data-testid="image-error"><span className="text-klyroo-red font-mono">✕ {err.code}</span> <span className="text-slate-400">{err.message}</span></div>}

        {!result ? (
          <div className="flex gap-2">
            <button onClick={generate} disabled={!ready || busy || !prompt.trim()} data-testid="image-generate-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-klyroo-cyan text-black text-sm font-semibold hover:bg-white disabled:opacity-50">{busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}{busy ? "Generating…" : "Generate Image"}</button>
            <button onClick={onClose} data-testid="image-cancel-btn" className="px-4 py-2 rounded-lg text-slate-300 text-sm hover:bg-white/5">Cancel</button>
          </div>
        ) : (
          <div data-testid="image-preview">
            <img src={result.image_url} alt="AI generated preview" data-testid="image-preview-img" className="w-full rounded-lg border border-white/10 mb-2" />
            <div className="text-[10px] font-mono text-slate-500 mb-3" data-testid="image-preview-meta">{result.provider}/{result.model} · {result.width}×{result.height} {result.format} · {result.execution_ms}ms</div>
            <div className="flex flex-wrap gap-2">
              <button onClick={use} disabled={applying} data-testid="image-use-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-emerald-400/20 text-emerald-200 text-sm font-semibold hover:bg-emerald-400/30 disabled:opacity-50">{applying ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Use Image</button>
              <button onClick={() => { setResult(null); generate(); }} disabled={busy} data-testid="image-regenerate-btn" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-white/5 text-slate-200 text-sm hover:bg-white/10 disabled:opacity-50"><RefreshCw size={14} /> Regenerate</button>
              <button onClick={onClose} data-testid="image-preview-cancel" className="px-4 py-2 rounded-lg text-slate-300 text-sm hover:bg-white/5 ml-auto">Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
